import { useState, useMemo } from 'react';
import {
  Box,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiSearch } from 'react-icons/fi';
import { useAllBounties } from '../../hooks/useAdmin';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { StatusBadge } from '../common/StatusBadge';
import { formatRelativeTime } from '../../utils/formatters';
import { Price } from '../common/Price';

export default function AdminBounties() {
  const { bounties, loading } = useAllBounties();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const filtered = useMemo(() => {
    let result = bounties;
    if (statusFilter !== 'all') {
      result = result.filter((b) => b.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (b) =>
          b.title?.toLowerCase().includes(q) ||
          b.creatorAnonymizedUsername?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [bounties, search, statusFilter]);

  if (loading) return <LoadingSpinner message="Loading tasks..." />;

  return (
    <VStack spacing={6} align="stretch">
      <Box>
        <Heading size="lg">Tasks</Heading>
        <Text color="gray.500" mt={1}>{bounties.length} total tasks</Text>
      </Box>

      <HStack spacing={3}>
        <InputGroup maxW="350px">
          <InputLeftElement pointerEvents="none">
            <Icon as={FiSearch} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search by title or creator..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <Select maxW="180px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_review">In Review</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </Select>
      </HStack>

      <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
        <Box overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Title</Th>
                <Th>Creator</Th>
                <Th>Reward</Th>
                <Th>Status</Th>
                <Th>Submissions</Th>
                <Th>Created</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((bounty) => (
                <Tr key={bounty.id}>
                  <Td maxW="250px">
                    <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{bounty.title}</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600">{bounty.creatorAnonymizedUsername}</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm"><Price cents={bounty.rewardAmount} size="sm" /></Text>
                  </Td>
                  <Td><StatusBadge status={bounty.status} /></Td>
                  <Td><Text fontSize="sm">{bounty.claimCount || 0}</Text></Td>
                  <Td>
                    <Text fontSize="xs" color="gray.500">{formatRelativeTime(bounty.createdAt)}</Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
        {filtered.length === 0 && (
          <Box py={8} textAlign="center">
            <Text color="gray.500">No tasks found</Text>
          </Box>
        )}
      </Box>

    </VStack>
  );
}
